/* @flow */

import * as React from 'react';

import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './Dropdown.style';

import {
  getDropdownOptionLabel,
  getDropdownOptionValue,
} from './util/dropdownUtils';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ChevronIconDown from 'src/shared/ui/component/icon/ChevronIconDown';

import type { DropdownOption } from './type/dropdownTypes';
import classNames from 'classnames';

type Props = {
  layoutClass?: string,
  id?: string,
  selectedValue?: string,
  options: Array<DropdownOption>,
  isDisabled?: boolean,
  multiple?: boolean,
  onBlur?: (event: HTMLInputElement | HTMLTextAreaElement) => void,
  onValueChange?: (string) => void,
  onChange?: (SyntheticInputEvent<HTMLSelectElement>) => void,
  extra?: string | React.Node,
  labelId: string,
};

const useStyles = createUseThemeStyles(styles);

const Dropdown = ({
  layoutClass,
  id,
  options,
  multiple,
  selectedValue,
  onValueChange,
  isDisabled,
  onChange,
  onBlur,
  extra,
  labelId,
  ...props
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const handleChange = (event: SyntheticInputEvent<HTMLSelectElement>) => {
    const { target } = event;

    if (onChange) onChange(event);
    if (onValueChange) onValueChange(target.value);
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */
  const optionElts = React.useMemo(
    () =>
      options.map((optionValue) => {
        const value = getDropdownOptionValue(optionValue);
        const label = getDropdownOptionLabel(optionValue);

        return (
          <MenuItem value={value} key={value}>
            {label}
          </MenuItem>
        );
      }),
    [options],
  );

  const getValue = () => {
    if (selectedValue) return selectedValue;
    if (!multiple) return '';
    return [];
  };

  return (
    <div className={classNames(classes.root, layoutClass)}>
      <div className={classes.wrapper}>
        <FormControl fullWidth variant="outlined">
          <Select
            {...props}
            disabled={isDisabled}
            displayEmpty
            id={id}
            onBlur={onBlur}
            value={getValue()}
            onChange={handleChange}
            multiple={multiple}
            IconComponent={ChevronIconDown}
            data-testid="dropdown-component"
            labelId={labelId}
          >
            {optionElts}
          </Select>
        </FormControl>
      </div>
      {extra && <div className={classes.extra}>{extra}</div>}
    </div>
  );
};

export default React.memo<Props>(Dropdown);
